/**
 * Settings → Data model → a field's menu → "Add to my data model…": put this field of the discovered model into one of the
 * data models of your own, as another source of one of its fields or as a field of its own. Saves the draft at once.
 * The window is a step in history (`?addto=` in the URL); the typed name and label are kept in sessionStorage.
 */
import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Api, ApiError, type VirtualModel } from '../lib/api.ts';
import { useAsync } from '../lib/hooks.ts';
import { useFormDraft } from '../lib/formstate.ts';
import { noun } from '../lib/nouns.ts';
import { LIMITS, addDraftKey, addToModel, buildPayload, hasSource, suggestLabel, systemOfClass, validate, type Source } from '../lib/modeledit.ts';
import { IconButton, SystemBadge, cx } from '../design/primitives.tsx';
import { CloseIcon } from './icons.tsx';
import { Button, Field, Spinner, inputClass } from './ui.tsx';

const NEW = 'new';

function Form({ tid, source, leaf, models, onClose, onSaved }: {
  tid: string; source: Source; leaf: string; models: VirtualModel[]; onClose: () => void; onSaved: (m: VirtualModel) => void;
}) {
  const initial = { model: models[0]?.id ?? NEW, name: '', field: NEW, label: suggestLabel(leaf) };
  const [d, setD, clear] = useFormDraft(addDraftKey(tid, source.cls, source.path), initial);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const model = d.model === NEW ? undefined : models.find((m) => m.id === d.model) ?? models[0];
  const modelId = model ? model.id : NEW;
  const fieldIndex = model && d.field !== NEW ? model.fields.findIndex((f) => f.label === d.field) : -1;
  const field = fieldIndex >= 0 ? model!.fields[fieldIndex] : undefined;
  const already = field ? hasSource(field, source) : false;
  const base = model?.fields ?? [];
  const name = model ? model.name : d.name;
  const result = addToModel(base, field ? { field: fieldIndex } : { label: d.label }, source);
  const problem = already ? `${source.path} is already a source of “${field!.label}”.`
    : result.result === 'label-taken' ? `${model?.name ?? 'This model'} already has a field called “${d.label.trim()}”: pick it above instead.`
      : result.result === 'full' ? `“${field!.label}” already has ${LIMITS.sources} sources.`
        : !model && !d.name.trim() ? 'Give the new data model a name'
          : (() => { const v = validate({ name, fields: result.fields }); return v.name ?? v.perField.find((p) => p.label)?.label; })();
  const save = async () => {
    if (problem) return;
    setBusy(true);
    setFailure(undefined);
    try {
      const saved = await Api.saveVirtualModel(tid, buildPayload(model?.id, { name, fields: result.fields }));
      clear();
      onSaved(saved);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void save(); }} noValidate>
      <div className="rounded-chip bg-n3 px-3 py-2.5 flex items-center gap-2.5 min-w-0">
        <SystemBadge code={source.system ?? systemOfClass(source.cls)} size={20} />
        <span className="font-mono text-small [overflow-wrap:anywhere]"><span className="text-n8">{source.cls}</span><span className="text-n6">·</span>{source.path}</span>
        <span className="text-micro text-n7 whitespace-nowrap">{noun(source.cls)}</span>
      </div>
      <Field label="Data model">
        <select className={inputClass} value={modelId} onChange={(e) => setD({ model: e.target.value, field: NEW })} autoFocus>
          {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          <option value={NEW}>New data model…</option>
        </select>
      </Field>
      {!model && (
        <Field label="Name of the new data model" error={d.name.length > LIMITS.name ? `At most ${LIMITS.name} characters` : undefined}>
          <input className={inputClass} value={d.name} placeholder="Customer" onChange={(e) => setD({ name: e.target.value })} />
        </Field>
      )}
      <fieldset className="m-0 p-0 border-0 flex flex-col gap-2 min-w-0">
        <legend className="p-0 mb-1 text-small text-n8">Where does it go?</legend>
        {model && model.fields.length > 0 && (
          <Field label="As another source of the field" className="[&>span:first-child]:sr-only">
            <select className={inputClass} value={field ? field.label : NEW} onChange={(e) => setD({ field: e.target.value })} aria-label="Field of the data model">
              {model.fields.map((f) => <option key={f.label} value={f.label}>{f.label}{hasSource(f, source) ? ' (already a source)' : ` · ${f.paths.length} source${f.paths.length === 1 ? '' : 's'}`}</option>)}
              <option value={NEW}>New field…</option>
            </select>
          </Field>
        )}
        {!field && (
          <Field label="Label of the new field" error={d.label.trim() === '' ? 'Give the field a label' : undefined}>
            <input className={inputClass} value={d.label} onChange={(e) => setD({ label: e.target.value })} />
          </Field>
        )}
        {field && field.paths.length > 0 && (
          <p className="m-0 text-micro text-n7">Now: {field.paths.map((p) => `${p.cls}·${p.path}`).join(', ')}</p>
        )}
      </fieldset>
      <div className="flex items-center gap-3 flex-wrap pt-1">
        <span className="flex-1 min-w-[200px] text-small" aria-live="polite">
          {failure ? <span className="text-fail">{failure}</span> : problem ? <span className={cx(already || result.result === 'label-taken' ? 'text-warn' : 'text-n7')}>{problem}</span>
            : <span className="text-n7">Saved to the draft of {name.trim() || 'the new model'}; publish it to version it.</span>}
        </span>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={busy || !!problem}>{busy ? 'Saving…' : model ? 'Add' : 'Create and add'}</Button>
      </div>
    </form>
  );
}

export function AddToModel({ tid, cls, path, leaf, onClose, onSaved }: {
  tid: string; cls: string; path: string; leaf: string; onClose: () => void; onSaved: (saved: VirtualModel, added: string) => void;
}) {
  const models = useAsync(() => Api.virtualModels(tid), [tid]);
  const source = useMemo<Source>(() => ({ cls, path, system: systemOfClass(cls) }), [cls, path]);
  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <Dialog.Content aria-describedby={undefined}
          className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,94vw)] max-h-[88vh] overflow-y-auto flex flex-col gap-4 bg-n2 text-n10 rounded-card shadow-float p-5 outline-none mv-pop">
          <div className="flex items-start gap-3">
            <Dialog.Title className="flex-1 m-0 text-body font-semibold">Add to my data model</Dialog.Title>
            <Dialog.Close asChild><IconButton label="Close"><CloseIcon size={16} /></IconButton></Dialog.Close>
          </div>
          {models.loading && !models.data && <div className="py-6 grid place-items-center"><Spinner /></div>}
          {models.error && <p className="m-0 text-small text-fail">{models.error}</p>}
          {models.data && <Form tid={tid} source={source} leaf={leaf} models={models.data} onClose={onClose} onSaved={(m) => onSaved(m, m.name)} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
